import { createTheme } from "@mui/material";
import { enUS, esES, ptBR } from "@mui/x-data-grid/locales";

const localesMap = {
  en: enUS,
  "pt-BR": ptBR,
  es: esES,
};

export function buildTheme(language: string) {
  return createTheme(
    {
      palette: {
        primary: {
          main: "#007A60",
        },
      },
      spacing: 8,
      typography: {
        fontFamily: "Nunito Sans",
      },
      components: {
        MuiButton: {
          variants: [
            {
              props: { variant: "switch" },
              style: {
                borderRadius: 8,
                border: "unset",
                color: "#1B1B1B",
                background: "#FFFFFF",
                "&:disabled": {
                  background: "#35423C",
                  color: "#FFFFFF",
                },
              },
            },
            {
              props: props => props.variant === "outlined" && props.color !== "error",
              style: {
                "&:hover": {
                  border: "1px solid var(--primary-mint-100)",
                  background: "unset",
                },
                "&:focus": {
                  border: "1px solid var(--primary-mint-400)",
                  background: "unset",
                },
                "&:active": {
                  border: "1px solid var(--primary-mint-600)",
                  background: "unset",
                },
                "&:disabled": {
                  border: "1px solid var(--soft-black-gray-scale-500)",
                  background: "var(--soft-black-gray-scale-200)",
                  color: "var(--soft-black-gray-scale-500)",
                },
              },
            },
            {
              props: { variant: "destructive" },
              style: {
                border: "1px solid var(--Validation-Error, #E61E1E)",
                "&:hover": {
                  border: "1px solid var(--Validation-Pink-Red, #FE8282)",
                  background: "unset",
                },
                "&:focus": {
                  border: "1px solid var(--Validation-Dark-Red, #C51414)",
                  background: "unset",
                },
                "&:active": {
                  border: "1px solid var(--Validation-Dark-Red, #C51414)",
                  background: "unset",
                },
                "&:disabled": {
                  border: "1px solid var(--soft-black-gray-scale-500)",
                  background: "var(--soft-black-gray-scale-200)",
                  color: "var(--soft-black-gray-scale-500)",
                },
              },
            },
            {
              props: props => props.variant === "contained" && props.color !== "error",
              style: {
                border: "1px solid var(--primary-mint-400)",
                background: "var(--primary-mint-200)",
                color: "var(--soft-black-gray-scale-900)",
                boxShadow: "unset",
                "&:hover": {
                  border: "1px solid var(--primary-mint-200)",
                  background: "var(--primary-mint-100)",
                  boxShadow: "unset",
                },
                "&:focus": {
                  border: "1px solid var(--primary-mint-400)",
                  background: "var(--primary-mint-300)",
                  boxShadow: "unset",
                },
                "&:active": {
                  border: "1px solid var(--primary-mint-600)",
                  background: "var(--primary-mint-500)",
                  boxShadow: "unset",
                },
                "&:disabled": {
                  border: "1px solid var(--soft-black-gray-scale-300)",
                  background: "var(--soft-black-gray-scale-200)",
                  color: "var(--soft-black-gray-scale-500)",
                },
              },
            },
            {
              props: { variant: "text" },
              style: {
                border: "0",
                color: "var(--soft-black-gray-scale-900)",
                boxShadow: "unset",
                "&:hover": {
                  color: "var(--primary-mint-200)",
                  background: "unset",
                },
                "&:focus": {
                  color: "var(--primary-mint-300)",
                  background: "unset",
                },
                "&:active": {
                  color: "var(--primary-mint-700)",
                  background: "unset",
                },
                "&:disabled": {
                  // NOTE: I've disabled this because there shouldn't be any reason
                  // for disabled text to have a background color, the text color change is enough
                  // - Augustinas
                  // background: "var(--soft-black-gray-scale-200)",
                  color: "var(--soft-black-gray-scale-500)",
                },
              },
            },
            {
              props: { variant: "danger" },
              style: {
                marginBottom: "20px",
                borderRadius: "8px",
                border: "var(--soft-black-gray-scale-100-white)",
                background: "var(--error-color)",
                color: "var(--soft-black-gray-scale-100-white)",
              },
            },
          ],
          styleOverrides: {
            root: {
              fontWeight: "400",
              textTransform: "none",
              display: "inline-flex",
              padding: "10px 24px",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "32px",
              lineHeight: "20px",
              variants: [
                {
                  // Mui button error variant has it's own custom styling that we want to respect.
                  props: props => props.color !== "error",
                  style: {
                    color: "var(--soft-black-gray-scale-900)",
                    border: "1px solid var(--primary-mint-200)",
                  },
                },
              ],
            },
            sizeSmall: {
              fontSize: "12px",
              paddingLeft: "16px",
              paddingRight: "16px",
              paddingTop: "2px",
              paddingBottom: "2px",
              lineHeight: "16px",
            },
          },
        },
        MuiAppBar: {
          styleOverrides: {
            root: {
              height: "72px !important",
              minHeight: "72px !important",
            },
            colorPrimary: {
              backgroundColor: "var(--dark-gray-900);",
            },
          },
        },
      },
    },
    language in localesMap ? localesMap[language as keyof typeof localesMap] : enUS,
  );
}
